import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { FilePenLine, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import {
  useCompletePortalConsultantContract,
  useInitiatePortalConsultantContract,
  usePortalConsultantContract,
} from '@alamo/api';
import { useAuth } from '@alamo/auth';
import { Button } from '@alamo/ui';

import { SignatureEmbed } from '../../pages/Public/MagicLink/components/SignatureEmbed';

import { LogoSvg } from './LogoSvg';

export function isContractCorrectionPath(pathname: string): boolean {
  return ['/my-registration', '/migrate', '/profile', '/consents', '/privacy'].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/** The iframe requests reconciliation; only the authenticated server state unlocks the CRM. */
export function ConsultantContractGate({
  children,
}: {
  children: (contractPending: boolean) => ReactNode;
}) {
  const { t } = useTranslation('auth');
  const { user, consultantType, logout, refreshUser } = useAuth();
  const location = useLocation();
  const isConsultant = consultantType === 'pf' || consultantType === 'pj';
  const query = usePortalConsultantContract(user?.id ?? '', isConsultant);
  const initiate = useInitiatePortalConsultantContract(user?.id ?? '');
  const { mutateAsync: completeContract } = useCompletePortalConsultantContract();
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionError, setCompletionError] = useState(false);
  const [embedError, setEmbedError] = useState(false);
  const completionStarted = useRef(false);
  const { refetch } = query;

  useEffect(() => {
    completionStarted.current = false;
    setCompletionError(false);
    setEmbedError(false);
  }, [user?.id]);

  useEffect(() => {
    if (!query.data?.needsCompletion) completionStarted.current = false;
  }, [query.data?.needsCompletion]);

  const completeAccess = useCallback(async () => {
    setIsCompleting(true);
    setCompletionError(false);
    try {
      await completeContract();
      await refreshUser();
      const result = await refetch();
      if (result.error || !result.data || result.data.isBlocked) setCompletionError(true);
    } catch {
      setCompletionError(true);
    } finally {
      setIsCompleting(false);
    }
  }, [completeContract, refreshUser, refetch]);

  useEffect(() => {
    if (
      isConsultant &&
      !query.isError &&
      query.isFetchedAfterMount &&
      query.data?.signed &&
      query.data.needsCompletion &&
      !completionStarted.current
    ) {
      completionStarted.current = true;
      void completeAccess();
    }
  }, [
    isConsultant,
    query.isError,
    query.isFetchedAfterMount,
    query.data?.signed,
    query.data?.needsCompletion,
    completeAccess,
  ]);

  if (!isConsultant) return children(false);
  const loaded = query.isFetchedAfterMount && Boolean(query.data);
  if (loaded && !query.isError && !isCompleting && !completionError && !query.data?.isBlocked) {
    return children(false);
  }
  if (loaded && !query.isError && query.data?.nextStep === 'registration') {
    if (!isContractCorrectionPath(location.pathname))
      return <Navigate to="/my-registration" replace />;
    return children(true);
  }

  const completing = isCompleting || query.data?.needsCompletion;
  const loading = !loaded && !query.isError;
  const contract = query.data?.contract ?? initiate.data;
  const hasEmbed =
    contract?.provider === 'zapsign'
      ? Boolean(contract.providerEnvelopeUrl)
      : Boolean(contract?.requestSignatureKey);
  const signingError = initiate.isError || embedError || Boolean(contract && !hasEmbed);

  return (
    <div className="min-h-screen bg-alamo-bg" data-testid="portal-contract-gate">
      <header className="flex items-center justify-between gap-4 bg-azul-protecao px-5 py-4 md:px-8">
        <LogoSvg className="h-8 w-auto md:h-10" />
        <Button
          variant="ghost"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={() => void logout()}
        >
          <LogOut size={16} aria-hidden="true" />
          {t('portal_contract.logout')}
        </Button>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 px-4 py-8 md:px-8 md:py-12">
        <div className="flex items-center gap-2 font-body text-sm text-azul-protecao">
          <ShieldCheck size={18} aria-hidden="true" />
          <span>{t('portal_contract.authenticated')}</span>
        </div>
        <div className="max-w-2xl space-y-3">
          <h1 className="font-heading text-2xl font-semibold leading-tight text-ink md:text-3xl">
            {t(
              loading
                ? 'portal_contract.loading'
                : completing
                  ? 'portal_contract.completing'
                  : 'portal_contract.title',
            )}
          </h1>
          <p className="font-body text-base leading-relaxed text-slate-600">
            {t('portal_contract.description')}
          </p>
        </div>
        <section
          className="space-y-5 rounded-2xl border border-slate-200 bg-alamo-surface p-5 md:p-8"
          style={{ boxShadow: 'var(--elev-1)' }}
        >
          {loading || (completing && !completionError) ? (
            <div
              role="status"
              className="flex items-center gap-3 py-8 font-body text-sm text-slate-600"
            >
              <Loader2 className="animate-spin text-azul-protecao" size={22} aria-hidden="true" />
              {t(completing ? 'portal_contract.confirming' : 'portal_contract.checking')}
            </div>
          ) : query.isError ? (
            <>
              <p role="alert" className="font-body text-sm text-erro-apoio">
                {t('portal_contract.status_error')}
              </p>
              <Button isLoading={query.isFetching} onClick={() => void refetch()}>
                {t('portal_contract.retry')}
              </Button>
            </>
          ) : completionError ? (
            <>
              <p role="alert" className="font-body text-sm text-erro-apoio">
                {t('portal_contract.completion_error')}
              </p>
              <Button onClick={() => void completeAccess()}>{t('portal_contract.retry')}</Button>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3 rounded-xl bg-laranja-encanto-04 p-4">
                <FilePenLine
                  className="mt-0.5 shrink-0 text-laranja-encanto"
                  size={22}
                  aria-hidden="true"
                />
                <div className="space-y-1 font-body text-sm">
                  <p className="font-semibold text-ink">{t('portal_contract.required')}</p>
                  <p className="leading-relaxed text-slate-600">
                    {t('portal_contract.requirement_hint')}
                  </p>
                </div>
              </div>
              {signingError && (
                <p role="alert" className="font-body text-sm text-erro-apoio">
                  {t('portal_contract.signature_error')}
                </p>
              )}
              {(!contract || signingError) && query.data?.canInitiate && (
                <Button
                  isLoading={initiate.isPending}
                  onClick={() => {
                    setEmbedError(false);
                    initiate.mutate();
                  }}
                >
                  {t(signingError ? 'portal_contract.retry' : 'portal_contract.open')}
                </Button>
              )}
              {contract && !signingError && !initiate.isPending && (
                <div className="min-h-[520px] overflow-hidden rounded-xl border border-slate-200">
                  <SignatureEmbed
                    provider={contract.provider}
                    requestSignatureKey={contract.requestSignatureKey}
                    envelopeUrl={contract.providerEnvelopeUrl}
                    onSigned={() => void refetch()}
                    onError={() => setEmbedError(true)}
                  />
                </div>
              )}
              <p className="font-body text-xs leading-relaxed text-slate-500">
                {t('portal_contract.return_hint')}
              </p>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
